import { useState, useEffect } from "react";
import "./FormContentScore.css";
import { Question, UpdateQuestionPayload } from "effno-core";
import { apiUpdateQuestion } from "../../services/QuestionService";
import { toastFail, toastSuccess } from "../../config";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { selectEditingForm, selectUIStates, setEditingForm } from "../../redux/features/appData";
import { useDispatch } from "react-redux";
import { SCORE_SETTINGS } from "../config/questionSettings";
import { DEBOUNCE_TIME } from "../config/forms";
import { apiFormHasEdits } from "../../services/FormService";

type FormContentScoreProps = {
    question: Question;
    allowSaving?: boolean;
}

export const FormContentScore = ({question, allowSaving = true} : FormContentScoreProps) => {

    const dispatch = useDispatch();

    const editingForm = useSelector(selectEditingForm);
    const uiStates = useSelector(selectUIStates);

    const [title, setTitle] = useState<string>("");
    const [description, setDescription] = useState<string>("");

    const [debouncedData, setDebouncedData] = useState<any | undefined>(undefined);
    const [debounceSettings, setDebounceSettings] = useState<any | undefined>(undefined);


    useEffect(() => {
        setTitle(question.title);
        setDescription(question.description);
    }, [question]);

    // Debounce settings changes
    useEffect(() => {
        const handler = setTimeout(() => {
            if (!debounceSettings) {
                return;
            }
            setDebouncedData(debounceSettings);
        }, DEBOUNCE_TIME);

        // Clear the timeout if settings changes (i.e., user is still typing)
        return () => {
            clearTimeout(handler);
        };
    }, [debounceSettings]);

    // Effect to save the debounced data whenever it changes
    useEffect(() => {
        (async () => {
            if (!debouncedData) {
                return;
            }

            console.log("debouncedData: ", debouncedData);
            
            await handleSave();
        })()
    }, [debouncedData]);

    useEffect(() => {
        setDebounceSettings({
            title,
            description,
        })
    }, [title, description])

    const updateRedux = () => {
        if (!editingForm) {
            return;
        }

        const index = editingForm.questions.findIndex((q: Question) =>  q.id === question.id);

        if (index === -1) {
            toast("Can't find question id", toastFail);
            return;
        }

        const newQuestions = [...editingForm.questions];
        newQuestions[index] = {
            ...question,
            title,
            description,
        };

        dispatch(setEditingForm({...editingForm, hasEdits: true, questions: newQuestions}));
    }

    const handleSave = async () => {
        if (!editingForm || !allowSaving) {
            return;
        }

        // update question in redux first to avoid delay lag causing issues
        updateRedux();

        const settings = question.settings ? JSON.parse(question.settings) : SCORE_SETTINGS;

        const payload : UpdateQuestionPayload = {
            questionId: question.id,
            type: question.type,
            title: title,
            description: description,
            required: question.required,
            settings: JSON.stringify(settings),  // these don't change in this instance
            conditions: question.conditions
        }

        const newQuestion = await apiUpdateQuestion(payload);

        if (!newQuestion) {
            toast("Error saving update", toastFail);
            return;
        }

        await apiFormHasEdits(editingForm.id);
        console.log("Question updated");

        // update redux
        // const index = editingForm.questions.findIndex((q: Question) =>  q.id === question.id);

        // if (index === -1) {
        //     toast("Can't find question id", toastFail);
        //     return;
        // }

        // const newQuestions = [...editingForm.questions];
        // newQuestions[index] = newQuestion;

        // await apiFormHasEdits(editingForm.id);

        // dispatch(setEditingForm({...editingForm, hasEdits: true, questions: newQuestions}));

        // console.log("Question updated");

    }

    const settings = JSON.parse(question.settings);

    const min = parseInt(settings.minScore) || 0;
    const max = parseInt(settings.maxScore) || 10; 

    const scoreArray : string [] = [];
    for (let i = min; i <= max; i++) {
        scoreArray.push(i.toString());
    }

    const gridRepeat = uiStates.mobileLayout ? "repeat(5, minmax(44px, 1fr))" : "repeat(11, minMax(44px, 1fr))";

    return (
        <div className="form-content-body">
            <div className="form-content-title">
                <input 
                    type="text"
                    placeholder="..."
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    data-testid="question-title"
                />
                {/* {question.required && <span>*</span>} */}
            </div>
            <div className="form-content-description">
                <input 
                    type="text"
                    placeholder="Description (optional)"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    data-testid="question-description"
                />
            </div>   

            <div 
                className="form-content-score"
                style={!uiStates.mobileLayout ? {marginTop: "24px"} : {}}
            >
                {/* <div className={uiStates.mobileLayout ? "form-content-scores-grid" : "form-content-scores"}> */}
                <div 
                    className="form-content-scores-grid"
                    style={{gridTemplateColumns: gridRepeat}}
                >

                    {scoreArray.map((score: string, index) => {
                        return (
                            <div key={score} className="form-content-score-item">
                                {score}
                            </div>
                        )
                    })}
                </div>
                <div className="form-content-score-labels">
                    <div className="form-content-score-labels-start">
                        {settings.leftLabelOn && settings.leftLabel}
                    </div>
                    <div className="form-content-score-labels-end">
                        {settings.rightLabelOn && settings.rightLabel}
                    </div>
                </div>
            </div>    

            {/* <button 
                onClick={handleSave}
                data-testid="save-question"
            >
                temp save button before debounce 
            </button> */}
        </div>
    )
}